import React, {useState, useEffect, useCallback} from 'react'
/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import useMediaQuery from '@mui/material/useMediaQuery'

import {connect} from 'react-redux'

import {useNavigate} from 'react-router-dom'

import {useIsMounted} from '../../../hooks'

import { 
  index,
  addRelationToX,
  setFetchedAssociations 
} from '../../../actions'

import { 
  makeGetArtistProducts,
  selectArtistFetchedAssociations 
} from '../../../selectors'

import AddIcon from '@mui/icons-material/Add'
import Dialog from '@mui/material/Dialog'

import {CardList, CardListMobileGrid} from '../../cards'

import {ProductForm} from '../../product'

import {
  GroupedButton,
  FadeTransition,
  SlideTransition,
  ThreeDotProgress,
  DetailPageTabMessage,
  DetailPageTabButtonContainer
} from '../../generics'



const styles = {
  root: css`
    position: absolute;
    height: 100%;
    width: 100%;
    display: flex;
    align-items: center;
    align-content: center;
    justify-content: flex-start;
    flex-direction: column;
    z-index: 50;
  `,
  emptyContainer: css`
    width: 100%;
    height: 20px;
  `,
  cardsContainer: css`
    width: 100%;
    flex-grow: 1;
  `,
  card: css`
    width: 200px;
    height: 330px;
    display: flex;
    flex-wrap: nowrap;
    flex-direction: column;
    border-radius: 6px;
    overflow: hidden;
    cursor: pointer;
    transition: 0.3s;
    box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 2px 0px, rgba(0, 0, 0, 0.15) 0px 1px 3px 1px;
  `,
  progressContainer: css`
    height: 100%;
    width: 100%;
    margin-top: 20px;
  `
}




const ArtistProducts = props => {

  const navigate = useNavigate()

  const isMounted = useIsMounted()

  const isExtraSmallScreen = useMediaQuery(theme => theme.breakpoints.only('xs'))


  const [isOpen, setIsOpen] = useState(false)


  const {
    index,
    links,
    isOwner,
    setLinks,
    artistId,
    products,
    isLoaded,
    isFetching,
    addRelationToX,
    setFetchedAssociations
  } = props


  const [isFetched, setIsFetched] = useState(isLoaded)



  const fetchMoreProducts = useCallback(props => {
    if (links.pages !== links.page) {
      const url = links.next_url.slice(4)

      index(url, 'PRODUCTS').then(response => {
        const products = response.data

        addRelationToX(products, 'products', artistId, 'artist')

        if (isMounted.current) {
          if (props.isMounted && props.isMounted.current && props.setIsFetching) {
            props.setIsFetching(false)
          }

          if (response.links) setLinks(response.links)

          setIsFetched(true)  
        }
      })
    }
  }, [
    addRelationToX,
    artistId,
    index,
    isMounted,
    links.next_url,
    links.page,
    links.pages,
    setLinks
  ])



  useEffect(() => {
    if (!isLoaded) {
      setFetchedAssociations('ARTIST', artistId, 'products')

      index('/merchants/artist/' + artistId + '/products', 'PRODUCTS').then(response => {
        const products = response.data

        addRelationToX(products, 'products', artistId, 'artist')

        if (isMounted.current) {
          if (response.links) setLinks(response.links)
          setIsFetched(true)
        }
      })
    }
  }, [
    isLoaded,
    artistId,
    index,
    addRelationToX,
    setFetchedAssociations,
    isMounted,
    setLinks
  ])



  function close() {
    setIsOpen(false)
  }


  function goToShop() {
    navigate('/shop?page=products')
  }




  return (
    <div css={styles.root}>
      <DetailPageTabButtonContainer>
        {isOwner && 
          <GroupedButton 
            text='New'
            tip='Add new product'
            icon={<AddIcon />}
            onClick={goToShop}  
          />
        }
      </DetailPageTabButtonContainer>



      <div css={styles.cardsContainer}>
        {Boolean(products.length) && 
          <>
            {isExtraSmallScreen &&
              <CardListMobileGrid
                type='product'
                cards={products}
                hasNextPage={links.pages !== links.page}
                fetchMoreItems={fetchMoreProducts}
              />
            }


            {!isExtraSmallScreen && 
              <CardList
                type='product'
                cards={products}
                hasNextPage={links.pages !== links.page}
                fetchMoreItems={fetchMoreProducts}
              /> 
            }
          </>
        }


        {!Boolean(products.length) && isFetched && 
          <DetailPageTabMessage>
            {isOwner 
              ? 'To add a product, click the plus icon above' 
              : 'This artist doesn\'t have any merchandise listed yet'
             }
          </DetailPageTabMessage>
        }




        {isFetching && 
          <div css={styles.progressContainer}>
            <ThreeDotProgress position='sticky' /> 
          </div>
        }
      </div> 


      <Dialog
        disableScrollLock
        fullWidth={true}
        fullScreen={true}
        maxWidth='lg'
        open={isOpen} 
        onClose={close}
        TransitionComponent={isExtraSmallScreen ? SlideTransition : FadeTransition}
      >
        <ProductForm 
          merchantId={artistId} 
          merchantType='artist' 
          setOpen={setIsOpen} 
        />
      </Dialog>
    </div>
  )
}



const makeMapStateToProps = () => {
  const getArtistProducts = makeGetArtistProducts()
  

  const mapStateToProps = (state, props) => {
    
    const id = props.artistId
    
    const products = getArtistProducts(state, id)


    return {
      products: products,
      isOwner: state.user.account.id === id,
      isLoaded: selectArtistFetchedAssociations(state, id).includes('products'),
    }
  }

  return mapStateToProps
}


const actions = {index, addRelationToX, setFetchedAssociations}



export default connect(makeMapStateToProps, actions)(ArtistProducts)

